import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Shield, Menu, X } from "lucide-react";

const TOC = [
  { id: "eligibility",           label: "Eligibility",                  num: "01" },
  { id: "account-security",      label: "Account Security",             num: "02" },
  { id: "services",              label: "Services Provided",            num: "03" },
  { id: "communications",        label: "Communications",               num: "04" },
  { id: "user-responsibilities", label: "User Responsibilities",        num: "05" },
  { id: "payment",               label: "Payment & Subscriptions",      num: "06" },
  { id: "ip",                    label: "Intellectual Property",        num: "07" },
  { id: "liability",             label: "Limitation of Liability",      num: "08" },
  { id: "termination",           label: "Termination",                  num: "09" },
  { id: "changes",               label: "Changes to These Terms",       num: "10" },
  { id: "contact",               label: "Contact",                      num: "11" },
];

function Section({ id, num, title, children }: { id: string; num: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 py-8 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-4 mb-4">
        <span className="flex-shrink-0 mt-0.5 text-xs font-mono font-bold text-[#0277BD] bg-blue-50 border border-blue-100 rounded-md px-2 py-1 tracking-widest">
          {num}
        </span>
        <h2 className="text-xl font-semibold text-gray-900 leading-snug">{title}</h2>
      </div>
      <div className="ml-12 space-y-3">{children}</div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 leading-7 text-[15px]">{children}</p>;
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-[15px] text-gray-600 leading-7">
          <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-[#0277BD] flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState<string>("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: "-15% 0px -75% 0px" }
    );
    TOC.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* Navigation bar */}
      <nav className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="h-9 w-9 rounded-xl bg-[#0277BD] flex items-center justify-center shadow-sm group-hover:bg-[#01579B] transition-colors">
              <span className="text-white font-bold text-lg" style={{ fontFamily: "Georgia, serif" }}>B</span>
            </div>
            <div className="hidden sm:block">
              <div className="text-gray-900 font-bold text-sm leading-none">301io</div>
              <div className="text-gray-400 text-xs leading-none mt-0.5">Home Protection</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/privacy" className="hidden sm:block text-sm text-gray-500 hover:text-gray-900 transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50">
              Privacy Policy
            </Link>
            <Link to="/auth/login" className="text-sm font-medium text-white bg-[#0277BD] hover:bg-[#01579B] transition-colors px-4 py-2 rounded-lg shadow-sm">
              Sign In
            </Link>
            <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setTocOpen(true)}>
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#01579B] via-[#0277BD] to-[#0288D1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-6">
            <Shield className="h-4 w-4" />
            <span>Legal</span>
            <span className="mx-1 opacity-50">/</span>
            <span className="text-white font-medium">Terms &amp; Conditions</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
            Terms &amp; Conditions
          </h1>
          <p className="text-blue-100 text-base sm:text-lg max-w-2xl leading-relaxed">
            Please read these terms carefully. By using 301io you agree to be bound by this agreement.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-sm">
              <span className="text-blue-200">Last updated</span>
              <span className="text-white font-medium">April 23, 2026</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-sm">
              <span className="text-blue-200">Effective</span>
              <span className="text-white font-medium">Upon account creation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile TOC drawer */}
      {tocOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setTocOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <span className="font-semibold text-gray-900">Contents</span>
              <button onClick={() => setTocOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <nav className="space-y-1">
              {TOC.map(({ id, label, num }) => (
                <a key={id} href={`#${id}`} onClick={() => setTocOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 group">
                  <span className="text-xs font-mono text-gray-400 group-hover:text-[#0277BD] w-6">{num}</span>
                  <span className="text-sm text-gray-700">{label}</span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-10 xl:gap-16">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-56 xl:w-64 flex-shrink-0" ref={headerRef}>
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4 px-3">Contents</p>
              <nav className="space-y-0.5">
                {TOC.map(({ id, label, num }) => (
                  <a key={id} href={`#${id}`}
                    className={`flex items-center gap-3 py-2 px-3 rounded-lg text-sm transition-all group ${
                      activeSection === id ? "bg-blue-50 text-[#0277BD] font-medium" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}>
                    <span className={`text-xs font-mono w-5 flex-shrink-0 ${activeSection === id ? "text-[#0277BD]" : "text-gray-300 group-hover:text-gray-400"}`}>{num}</span>
                    {label}
                  </a>
                ))}
              </nav>
              <div className="mt-8 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Questions?{" "}
                  <a href="mailto:legal@brandsmart.com" className="text-[#0277BD] hover:underline font-medium">Contact us</a>
                </p>
              </div>
            </div>
          </aside>

          {/* Document body */}
          <article className="flex-1 min-w-0 max-w-3xl">

            <div className="mb-10 p-5 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <p className="text-gray-700 leading-7 text-[15px]">
                Welcome to 301io ("Service", "we", "us"). By creating an account or using our mobile
                or web application, you agree to be bound by these Terms &amp; Conditions. Please read
                them carefully before proceeding.
              </p>
            </div>

            <Section id="eligibility" num="01" title="Eligibility">
              <P>
                You must be at least 18 years old to create a 301io account. By registering,
                you confirm that all information you provide is accurate, current, and complete.
              </P>
            </Section>

            <Section id="account-security" num="02" title="Account Security">
              <P>
                You are responsible for maintaining the confidentiality of your account credentials.
                Notify us immediately at{" "}
                <a href="mailto:support@brandsmart.com" className="text-[#0277BD] hover:underline font-medium">
                  support@brandsmart.com
                </a>{" "}
                if you suspect unauthorised access.
              </P>
            </Section>

            <Section id="services" num="03" title="Services Provided">
              <P>301io provides smart-home management tools including, but not limited to:</P>
              <List items={[
                "Asset & appliance tracking with warranty management",
                "Scheduled and on-demand home maintenance services",
                "AI-assisted appliance diagnostics",
                "Wi-Fi device discovery and network management",
                "Protection plans and claims processing",
                "In-home shopping through the 301io catalogue",
              ]} />
            </Section>

            <Section id="communications" num="04" title="Cell Number & Communications">
              <P>
                By providing your cell number you consent to receive transactional SMS messages
                (e.g. OTP codes, service booking confirmations, maintenance reminders). Standard
                message and data rates may apply. You may opt out at any time from your profile settings.
              </P>
            </Section>

            <Section id="user-responsibilities" num="05" title="User Responsibilities">
              <List items={[
                "Provide accurate home and appliance information.",
                "Do not misuse the AI diagnostic feature as a substitute for professional advice.",
                "Do not share your account with individuals who are not on your family plan.",
              ]} />
            </Section>

            <Section id="payment" num="06" title="Payment & Subscriptions">
              <P>
                Certain features (e.g. protection plans, premium service bookings) require payment.
                Charges are processed via Stripe and are non-refundable except as required by law
                or as stated in the individual service agreement.
              </P>
            </Section>

            <Section id="ip" num="07" title="Intellectual Property">
              <P>
                All content, trademarks, and technology within the 301io application are the
                exclusive property of 301io and may not be reproduced without written permission.
              </P>
            </Section>

            <Section id="liability" num="08" title="Limitation of Liability">
              <P>
                To the maximum extent permitted by applicable law, 301io shall not be liable
                for any indirect, incidental, or consequential damages arising from your use of the Service.
              </P>
            </Section>

            <Section id="termination" num="09" title="Termination">
              <P>
                We reserve the right to suspend or terminate accounts that violate these Terms,
                engage in fraudulent activity, or remain inactive for an extended period.
              </P>
            </Section>

            <Section id="changes" num="10" title="Changes to These Terms">
              <P>
                We may update these Terms from time to time. Continued use of the Service after
                changes are posted constitutes your acceptance of the revised Terms.
              </P>
            </Section>

            <Section id="contact" num="11" title="Contact">
              <P>
                Questions? Contact us at{" "}
                <a href="mailto:legal@brandsmart.com" className="text-[#0277BD] hover:underline font-medium">legal@brandsmart.com</a>
                {" "}or write to:
              </P>
              <address className="not-italic mt-3 p-4 rounded-xl bg-gray-50 border border-gray-100 text-sm text-gray-600 leading-7">
                <strong className="text-gray-900 font-semibold">301io</strong><br />
                3200 SW 42nd St<br />
                Fort Lauderdale, FL 33312, USA
              </address>
            </Section>

            {/* Footer */}
            <div className="mt-14 pt-8 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-6 w-6 rounded-md bg-[#0277BD] flex items-center justify-center">
                      <span className="text-white font-bold text-xs" style={{ fontFamily: "Georgia, serif" }}>B</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">301io</span>
                  </div>
                  <p className="text-xs text-gray-400">� {new Date().getFullYear()} All rights reserved.</p>
                </div>
                <div className="flex items-center gap-5 text-sm text-gray-400">
                  <Link to="/privacy" className="hover:text-[#0277BD] transition-colors">Privacy Policy</Link>
                  <Link to="/auth/login" className="hover:text-[#0277BD] transition-colors">Sign In</Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-6 right-6 z-30 h-11 w-11 rounded-full bg-[#0277BD] text-white shadow-lg flex items-center justify-center hover:bg-[#01579B] transition-all duration-300 ${
          showBackToTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="Back to top"
      >
        <ArrowUp className="h-4 w-4" />
      </button>
    </div>
  );
}
